import { computed, observable, runInAction } from "mobx"
import { toast } from "sonner"

import { branchDisplayName } from "@/lib/displayUtils.ts"
import { Model } from "@/lib/Model.ts"
import { copyToClipboard } from "@/lib/std.ts"
import { Navigator } from "@/routes/Navigator.ts"

import type { Queries } from "@/lib/queries.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { useNavigate } from "@tanstack/react-router"
import type { IObservableValue } from "mobx"

export class TaskMoreMenuModel extends Model {
  readonly dropdownOpen: IObservableValue<boolean>
  readonly confirmDialogOpen: IObservableValue<boolean>

  constructor(
    private readonly taskModel: TaskViewModel,
    private readonly navigate: ReturnType<typeof useNavigate>,
    private readonly queries: Queries
  ) {
    super("TaskMoreMenuModel")

    this.dropdownOpen = observable.box(false)
    this.confirmDialogOpen = observable.box(false)
  }

  readonly canCopyBranchName = computed(() => {
    const task = this.taskModel.task.get()
    const historyItems = this.taskModel.historyItems.get()
    return task && historyItems.length > 0 && task.resultBranch
  })

  readonly canDownloadPatch = computed(() => {
    const changedFiles = this.taskModel.changesModel.files.get()
    return changedFiles.length > 0
  })

  readonly canDuplicate = computed(() => {
    const task = this.taskModel.task.get()
    const historyItems = this.taskModel.historyItems.get()
    return task && historyItems.length > 0 && task.resultBranch
  })

  readonly canDelete = computed(() => {
    const task = this.taskModel.task.get()
    return task !== null
  })

  readonly canGenerateTaskDraft = computed(() => {
    const task = this.taskModel.task.get()
    return task !== null
  })

  getBranchName = (): string | null => {
    const task = this.taskModel.task.get()
    const historyItems = this.taskModel.historyItems.get()

    if (!task || historyItems.length === 0) return null

    const branchName = task.resultBranch
    if (!branchName) return null

    return branchDisplayName(branchName)
  }

  copyBranchName = () => {
    const branchName = this.getBranchName()
    if (!branchName) {
      toast.info("No branch name available")
      return
    }

    void copyToClipboard(branchName)
    runInAction(() => this.dropdownOpen.set(false))
  }

  downloadPatch = () => {
    this.taskModel.changesModel.downloadPatch()
    runInAction(() => this.dropdownOpen.set(false))
  }

  handleDuplicate = async () => {
    const task = this.taskModel.task.get()
    const historyItems = this.taskModel.historyItems.get()
    const changedFiles = this.taskModel.changesModel.files.get()
    const currentCommit = this.taskModel.currentVersionCommitId.get()

    if (!task || !task.resultBranch || historyItems.length === 0 || !currentCommit) return

    try {
      const newTask = await this.taskModel.duplicateTask(task, currentCommit, changedFiles)
      await this.navigate(Navigator.task(this.taskModel.orgId, this.taskModel.project.get().key, newTask.id))
    } catch {
      toast.error("Failed to duplicate idea")
    }

    runInAction(() => this.dropdownOpen.set(false))
  }

  handleDelete = async () => {
    runInAction(() => {
      this.confirmDialogOpen.set(false)
      this.dropdownOpen.set(false)
    })

    await this.taskModel.deleteTask()
    await this.navigate(Navigator.project(this.taskModel.orgId, this.taskModel.project.get().key))
  }

  generateTaskDraft = () => {
    const task = this.taskModel.task.get()
    if (!task) return

    const taskTitle = task.title || "Untitled Task"
    const description = task.functionalSummary || "–"
    const branchName = this.getBranchName() || "N/A"
    const taskLink = this.taskModel.taskLink
    const dodContent = task.definitionOfDone || "N/A"

    try {
      const markdown = `**Title**\n${taskTitle}\n\n**Description**\n${description}\n\n**Task link**\n${taskLink}\n\n**Branch name**\n${branchName}\n\n**Definition of Done**\n${dodContent}`
      void copyToClipboard(markdown)
      void this.queries.apiClient.reportTaskDraftGeneratedEvent(task.id)
      runInAction(() => this.dropdownOpen.set(false))
    } catch {
      runInAction(() => this.dropdownOpen.set(false))
    }
  }
}